import { useState } from 'react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import FeedbackModal from '@/components/FeedbackModal';
import { useGetActiveBitsQuery } from '@/redux/bit/bit.api.slice';
import { IBitPublic, IBitBrandRef } from '@/redux/bit/bit.d';
import { ILaserOrEndmill, IBitType } from '@/redux/post/post.d';

// Four independent optional spec fields — inherently branchy display
// formatting, not logic. Splitting it only scatters the same branches.
// fallow-ignore-next-line complexity
function bitSpecs(b: IBitPublic): string[] {
  return [
    b.toolUsed != null ? `${b.toolUsed} ${b.toolUnit ?? ''}`.trim() : null,
    b.vBitDegree != null ? `${b.vBitDegree}°` : null,
    b.tipRadius != null ? `⌀ ${b.tipRadius} ${b.toolUnit ?? ''}`.trim() : null,
    b.wattage != null ? `${b.wattage} W` : null,
  ].filter((s): s is string => Boolean(s));
}

// One catalog entry per brand: a bit with N brands yields N items; a bit with
// no brands yields a single brand-less item.
type BitItem = { bit: IBitPublic; brand?: IBitBrandRef };

function toItems(bits: IBitPublic[]): BitItem[] {
  return bits.flatMap((bit) =>
    bit.brands.length ? bit.brands.map((brand) => ({ bit, brand })) : [{ bit }]
  );
}

function itemSearchText(item: BitItem): string {
  return [
    item.bit.name,
    item.bit.bitType,
    item.brand?.name,
    ...bitSpecs(item.bit),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

function BitThumb({ bit, size }: { bit: IBitPublic; size: string }) {
  return bit.imageUrl ? (
    <img
      src={bit.imageUrl}
      alt=""
      className={cn(
        'object-contain rounded bg-white border flex-shrink-0',
        size
      )}
    />
  ) : (
    <div className={cn('rounded bg-muted flex-shrink-0', size)} />
  );
}

function TypeChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
        active
          ? 'border-primary bg-primary text-primary-foreground'
          : 'border-[#D6D6D6] text-muted-foreground hover:bg-muted'
      )}
    >
      {label}
    </button>
  );
}

// Selected-state styling plus brand/type/specs optional lines — display glue,
// not logic; splitting it only scatters the same conditionals.
// fallow-ignore-next-line complexity
function BitCard({
  item,
  selected,
  onSelect,
}: {
  item: BitItem;
  selected: boolean;
  onSelect: () => void;
}) {
  const { bit, brand } = item;
  const specs = bitSpecs(bit);
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'flex items-center gap-3 rounded-md border p-3 text-left transition-colors hover:bg-muted/60',
        selected
          ? 'border-primary ring-1 ring-primary bg-muted/40'
          : 'border-[#D6D6D6]'
      )}
    >
      <BitThumb bit={bit} size="w-12 h-12" />
      <div className="min-w-0">
        <div className="text-sm font-medium truncate">{bit.name}</div>
        {brand && (
          <div className="text-xs font-medium text-primary truncate">
            {brand.name}
          </div>
        )}
        {bit.bitType && (
          <div className="text-xs text-muted-foreground truncate">
            {bit.bitType}
          </div>
        )}
        {specs.length > 0 && (
          <div className="text-xs text-muted-foreground truncate">
            {specs.join(' • ')}
          </div>
        )}
      </div>
    </button>
  );
}

/**
 * BitPicker: a select-like trigger that opens a catalog modal. Browsing (search
 * + type filter + image cards) happens inside the dialog, so the form field
 * stays a compact summary of the picked bit. Same API as before — only the bit
 * itself is persisted; the type filter is transient dialog state.
 *
 * Branch count below is render conditionals (trigger summary + search + type
 * filter + results), not logic — decomposing would only scatter it.
 */
// fallow-ignore-next-line complexity
export function BitPicker({
  value,
  brandValue,
  laserOrEndmill,
  placeholder = 'Select a bit from the catalog',
  onChange,
}: {
  value?: string;
  brandValue?: string;
  laserOrEndmill: ILaserOrEndmill;
  placeholder?: string;
  onChange: (bit: IBitPublic | null, brand?: IBitBrandRef | null) => void;
}) {
  const { data: bits = [], isLoading } = useGetActiveBitsQuery();
  const [open, setOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<IBitType | undefined>(undefined);

  const catalog = bits.filter((b) => b.laserOrEndmill === laserOrEndmill);
  const selectedBit = catalog.find((b) => b._id === value) ?? null;
  const selectedBrand =
    selectedBit?.brands.find((b) => b._id === brandValue) ?? null;

  // Type chips (Endmill only): types present in the catalog, canonical order.
  const types = Object.values(IBitType).filter((t) =>
    catalog.some((b) => b.bitType === t)
  );

  const q = query.trim().toLowerCase();
  const shown = toItems(catalog)
    .filter((it) => !typeFilter || it.bit.bitType === typeFilter)
    .filter((it) => !q || itemSearchText(it).includes(q));

  const noun = laserOrEndmill === ILaserOrEndmill.LASER ? 'laser' : 'tool bit';

  return (
    <>
      {/* Trigger — a select-like control; the picked bit summary or placeholder */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={isLoading}
        className="flex w-full items-center gap-3 rounded-md border border-[#D6D6D6] bg-white px-3 py-2 text-left transition-colors hover:border-neutral-400 disabled:opacity-60"
      >
        {selectedBit ? (
          <>
            <BitThumb bit={selectedBit} size="w-9 h-9" />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate">
                {selectedBit.name}
              </div>
              <div className="text-xs text-muted-foreground truncate">
                {[
                  selectedBrand?.name,
                  selectedBit.bitType,
                  ...bitSpecs(selectedBit),
                ]
                  .filter(Boolean)
                  .join(' • ')}
              </div>
            </div>
            <span className="text-xs text-muted-foreground flex-shrink-0">
              Change
            </span>
          </>
        ) : (
          <span className="flex-1 text-sm text-muted-foreground">
            {isLoading ? 'Loading catalog…' : placeholder}
          </span>
        )}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Select a {noun}</DialogTitle>
            <DialogDescription>
              Search by name or size
              {types.length ? ', or filter by type' : ''}.
            </DialogDescription>
          </DialogHeader>

          <Input
            autoFocus
            placeholder={`Search ${catalog.length} ${noun}s…`}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />

          {types.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              <TypeChip
                label="All"
                active={!typeFilter}
                onClick={() => setTypeFilter(undefined)}
              />
              {types.map((t) => (
                <TypeChip
                  key={t}
                  label={t}
                  active={typeFilter === t}
                  onClick={() => setTypeFilter(t)}
                />
              ))}
            </div>
          )}

          <div className="grid max-h-[55vh] grid-cols-1 gap-2 overflow-y-auto pr-1 sm:grid-cols-2">
            {shown.map((it) => (
              <BitCard
                key={it.brand ? `${it.bit._id}:${it.brand._id}` : it.bit._id}
                item={it}
                selected={it.bit._id === value}
                onSelect={() => {
                  onChange(it.bit, it.brand ?? null);
                  setOpen(false);
                }}
              />
            ))}
            {shown.length === 0 && (
              <div className="col-span-full py-8 text-center text-sm text-muted-foreground">
                No {noun}s match your search.
              </div>
            )}
          </div>

          <div className="pt-1 text-center">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                setFeedbackOpen(true);
              }}
              className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
            >
              Can&apos;t find your {noun}? Request a missing {noun}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <FeedbackModal
        open={feedbackOpen}
        onOpenChange={setFeedbackOpen}
        initialSubject={`Missing ${noun}: `}
      />
    </>
  );
}
